import { useEffect, useRef } from 'react';

import { captureMessage } from '@sentry/react';
import hasIn from 'lodash-es/hasIn';
import { useSnackbar } from 'notistack';
import type { FieldPath, FieldValues, UseFormReturn } from 'react-hook-form';

import type { BackendErrorResponse } from 'api/types';

import { parseBackendInvalidParams } from './parseBackendInvalidParams';

export type BackendFormFieldErrorEvent<TFieldValues extends FieldValues> = {
  path: FieldPath<TFieldValues>;
};

export type BackendFieldAliases<TFieldValues extends FieldValues> = Record<string, FieldPath<TFieldValues>>;

type UseBackendFormErrorsParams<TFieldValues extends FieldValues> = {
  formMethods: UseFormReturn<TFieldValues>;
  error: BackendErrorResponse | null | undefined;
  /**
   * Maps a backend field name to the registered RHF field that should receive its error.
   *
   * Use only when the FE field holds an object value (e.g. an autocomplete result) and a
   * rename would lose information. For scalar 1:1 matches, rename the FE field to match
   * the BE name instead — no alias needed.
   *
   * @example
   * ```tsx
   * fieldAliases={{ company_name: 'company', provider_name: 'payrollProvider' }}
   * ```
   */
  fieldAliases?: BackendFieldAliases<TFieldValues>;
  onFieldError?: (event: BackendFormFieldErrorEvent<TFieldValues>) => void;
  parseBackendErrorMessage?: (error: BackendErrorResponse) => string | undefined;
};

type ClassifiedBackendError<TFieldValues extends FieldValues> = {
  knownFieldErrors: { path: FieldPath<TFieldValues>; message: string }[];
  unknownFieldErrors: { path: string; message: string }[];
  snackbarMessage: string | undefined;
};

const classifyBackendError = <TFieldValues extends FieldValues>(
  error: BackendErrorResponse,
  formValues: TFieldValues,
  parseBackendErrorMessage?: (error: BackendErrorResponse) => string | undefined,
  fieldAliases?: BackendFieldAliases<TFieldValues>,
): ClassifiedBackendError<TFieldValues> => {
  const { fieldErrors, unmappedMessages } = parseBackendInvalidParams(error);

  const knownFieldErrors: ClassifiedBackendError<TFieldValues>['knownFieldErrors'] = [];
  const unknownFieldErrors: ClassifiedBackendError<TFieldValues>['unknownFieldErrors'] = [];
  const snackbarMessages: string[] = [];

  for (const { path, message } of fieldErrors) {
    const resolvedPath = fieldAliases?.[path] ?? path;
    if (hasIn(formValues, resolvedPath)) {
      knownFieldErrors.push({ path: resolvedPath as FieldPath<TFieldValues>, message });
      continue;
    }
    unknownFieldErrors.push({ path, message });
    if (message) {
      snackbarMessages.push(message);
    }
  }

  for (const message of unmappedMessages) {
    if (message) {
      snackbarMessages.push(message);
    }
  }

  if (fieldErrors.length === 0 && unmappedMessages.length === 0) {
    const errorMessage = parseBackendErrorMessage?.(error);
    if (errorMessage) {
      snackbarMessages.push(errorMessage);
    }
  }

  return { knownFieldErrors, unknownFieldErrors, snackbarMessage: snackbarMessages[0] };
};

const reportUnknownField = (path: string, message: string) => {
  captureMessage('Backend validation error on unknown form field', {
    level: 'warning',
    extra: { backendField: path, backendMessage: message },
  });
};

export const useBackendFormErrors = <TFieldValues extends FieldValues>({
  formMethods,
  error,
  fieldAliases,
  onFieldError,
  parseBackendErrorMessage,
}: UseBackendFormErrorsParams<TFieldValues>) => {
  const { setError, getValues } = formMethods;
  const { enqueueSnackbar } = useSnackbar();

  const callbacksRef = useRef({ onFieldError, parseBackendErrorMessage, fieldAliases });
  callbacksRef.current = { onFieldError, parseBackendErrorMessage, fieldAliases };

  useEffect(() => {
    if (!error) {
      return;
    }

    const { knownFieldErrors, unknownFieldErrors, snackbarMessage } = classifyBackendError(
      error,
      getValues(),
      callbacksRef.current.parseBackendErrorMessage,
      callbacksRef.current.fieldAliases,
    );

    for (const { path, message } of unknownFieldErrors) {
      reportUnknownField(path, message);
    }

    if (snackbarMessage) {
      enqueueSnackbar(snackbarMessage, { variant: 'error' });
    }

    if (knownFieldErrors.length === 0) {
      return;
    }

    callbacksRef.current.onFieldError?.({ path: knownFieldErrors[0].path });

    knownFieldErrors.forEach(({ path, message }, index) => {
      setError(path, { type: 'server', message }, index === 0 ? { shouldFocus: true } : undefined);
    });
  }, [error, getValues, setError, enqueueSnackbar]);
};
